import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  TrendingUp, MapPin, Star, Share2, AlertTriangle, CheckCircle, Activity,
  Phone, PhoneIncoming, PhoneMissed, Shield, Search, MessageSquare,
  Bell, ArrowRight, Clock, Users, Zap, ChevronRight, Eye
} from 'lucide-react';

import GeoFencingUpsell from './GeoFencingUpsell';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ClientOverview({ client, getHeaders }) {
  const { t } = useTranslation('dashboard');
  const [data, setData] = useState(null);
  const [leads, setLeads] = useState(null);
  const [notifications, setNotifications] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const headers = getHeaders();
    Promise.all([
      fetch(`${API}/api/client/dashboard/overview`, { headers }).then(r => r.json()),
      fetch(`${API}/api/client/dashboard/leads-overview`, { headers }).then(r => r.json()).catch(() => null),
      fetch(`${API}/api/client/dashboard/notifications`, { headers }).then(r => r.json()).catch(() => null),
    ]).then(([overview, leadsData, notifData]) => {
      setData(overview);
      setLeads(leadsData);
      setNotifications(notifData);
    }).catch(console.error).finally(() => setLoading(false));
  }, [getHeaders]);

  if (loading) return <div className="text-gray-400 animate-pulse py-12 text-center">{t('overview.loading')}</div>;
  if (!data) return <div className="text-red-400 text-center py-12">{t('overview.loadFailed')}</div>;

  const listingsPct = data.listings.total > 0 ? Math.round(data.listings.synced / data.listings.total * 100) : 0;
  const coreScore = data.profile_strength.core;
  const additionalScore = data.profile_strength.additional;
  const totalScore = Math.round((coreScore * 0.6 + additionalScore * 0.4));
  const ps = data.products_status || {};

  return (
    <div className="space-y-6" data-testid="overview-tab">

      {/* ─── LEADS OVERVIEW ─── */}
      <section data-testid="leads-overview-section">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <PhoneIncoming className="h-4 w-4 text-blue-400" /> {t('overview.leadsOverview')}
            <Badge variant="secondary" className="bg-blue-900/40 text-blue-300 text-[10px] ml-1">{t('overview.last30Days')}</Badge>
          </h3>
        </div>
        {leads && leads.connected ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <LeadCard
              title={t('overview.totalLeads')}
              value={leads.total_leads}
              icon={Phone}
              color="blue"
              subtitle={t('overview.totalLeadsSub')}
              testid="lead-total"
            />
            <LeadCard
              title={t('overview.qualityLeads')}
              value={leads.quality}
              icon={CheckCircle}
              color="emerald"
              subtitle={t('overview.qualityLeadsSub')}
              pct={leads.total_leads > 0 ? Math.round(leads.quality / leads.total_leads * 100) : 0}
              testid="lead-quality"
            />
            <LeadCard
              title={t('overview.potentialLeads')}
              value={leads.potential}
              icon={Users}
              color="amber"
              subtitle={t('overview.potentialLeadsSub')}
              pct={leads.total_leads > 0 ? Math.round(leads.potential / leads.total_leads * 100) : 0}
              testid="lead-potential"
            />
            <LeadCard
              title={t('overview.spamJunk')}
              value={leads.spam}
              icon={Shield}
              color="red"
              subtitle={t('overview.spamJunkSub')}
              pct={leads.total_leads > 0 ? Math.round(leads.spam / leads.total_leads * 100) : 0}
              testid="lead-spam"
            />
          </div>
        ) : (
          <Card className="bg-white border-gray-200 border-dashed">
            <CardContent className="py-8 text-center">
              <Phone className="h-8 w-8 text-gray-600 mx-auto mb-3" />
              <p className="text-sm text-gray-400">{t('overview.callTrackingNotConnected')}</p>
              <p className="text-xs text-gray-600 mt-1">{t('overview.callTrackingHint')}</p>
            </CardContent>
          </Card>
        )}

        {/* Recent Leads */}
        {leads?.connected && leads?.recent_leads?.length > 0 && (
          <Card className="bg-white border-gray-200 mt-4" data-testid="recent-leads-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-gray-900 text-sm flex items-center gap-2">
                <Activity className="h-3.5 w-3.5 text-blue-400" /> {t('overview.recentCalls')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {leads.recent_leads.slice(0, 5).map((lead) => (
                  <div key={lead.id || lead.phone || lead.caller_number} className="flex items-center gap-3 py-2 border-b border-gray-200/50 last:border-0">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      lead.category === 'quality' ? 'bg-emerald-900/40' :
                      lead.category === 'spam' ? 'bg-red-900/40' : 'bg-amber-900/40'
                    }`}>
                      {lead.category === 'quality' ? <CheckCircle className="h-3.5 w-3.5 text-emerald-400" /> :
                       lead.category === 'spam' ? <Shield className="h-3.5 w-3.5 text-red-400" /> :
                       <Users className="h-3.5 w-3.5 text-amber-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-200 truncate">{lead.name || t('overview.unknownCaller')}</p>
                      <p className="text-[11px] text-gray-500">
                        {lead.city && lead.state ? `${lead.city}, ${lead.state}` : lead.source} &middot; {formatDuration(lead.duration)}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <Badge variant="secondary" className={`text-[10px] ${
                        lead.category === 'quality' ? 'bg-emerald-900/40 text-emerald-300' :
                        lead.category === 'spam' ? 'bg-red-900/40 text-red-300' : 'bg-amber-900/40 text-amber-300'
                      }`}>
                        {t(`overview.category.${lead.category}`, lead.category)}
                      </Badge>
                      {lead.first_call && <span className="block text-[10px] text-blue-400 mt-0.5">{t('overview.newCaller')}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </section>

      {/* ─── PRODUCTS STATUS ─── */}
      <section data-testid="products-status-section">
        <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2 mb-3">
          <Zap className="h-4 w-4 text-yellow-400" /> {t('overview.productsStatus')}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <ProductCard
            title={t('overview.products.listings')}
            icon={MapPin}
            status={ps.listings?.errors > 0 ? 'warning' : 'active'}
            statusLabel={t(`overview.status.${ps.listings?.errors > 0 ? 'warning' : 'active'}`)}
            metric={`${ps.listings?.active || 0}/${ps.listings?.total || 0}`}
            label={t('overview.products.listingsActive')}
            detail={ps.listings?.errors > 0 ? t('overview.products.listingsNeedAttention', { count: ps.listings.errors }) : t('overview.products.listingsAllSynced')}
            testid="product-listings"
          />
          <ProductCard
            title={t('overview.products.reviews')}
            icon={Star}
            status="active"
            statusLabel={t('overview.status.active')}
            metric={ps.reviews?.avg_rating ? `${ps.reviews.avg_rating}` : '—'}
            label={t('overview.products.reviewsTotal', { count: ps.reviews?.total || 0 })}
            detail={ps.reviews?.avg_rating >= 4 ? t('overview.products.reviewsRatingGreat') : ps.reviews?.avg_rating >= 3 ? t('overview.products.reviewsRatingGood') : t('overview.products.reviewsRatingNeedsWork')}
            testid="product-reviews"
          />
          <ProductCard
            title={t('overview.products.seo')}
            icon={Search}
            status={ps.seo?.avg_position && ps.seo.avg_position <= 10 ? 'active' : 'warning'}
            statusLabel={t(`overview.status.${ps.seo?.avg_position && ps.seo.avg_position <= 10 ? 'active' : 'warning'}`)}
            metric={ps.seo?.avg_position ? `#${ps.seo.avg_position}` : '—'}
            label={t('overview.products.seoAvgPosition')}
            detail={t('overview.products.seoMetric', { clicks: ps.seo?.clicks || 0, impressions: ps.seo?.impressions || 0 })}
            testid="product-seo"
          />
          <ProductCard
            title={t('overview.products.qa')}
            icon={MessageSquare}
            status={ps.questions?.unanswered > 0 ? 'warning' : 'active'}
            statusLabel={t(`overview.status.${ps.questions?.unanswered > 0 ? 'warning' : 'active'}`)}
            metric={`${ps.questions?.total || 0}`}
            label={t('overview.products.qaQuestions')}
            detail={ps.questions?.unanswered > 0 ? t('overview.products.qaUnanswered', { count: ps.questions.unanswered }) : t('overview.products.qaAllAnswered')}
            testid="product-qa"
          />
        </div>
      </section>

      {/* ─── PROFILE STRENGTH + NOTIFICATIONS ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Profile Strength */}
        <Card className="bg-white border-gray-200" data-testid="profile-strength-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-gray-900 text-sm">{t('overview.profileStrength')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">{t('overview.overall')}</span>
                  <span className="text-2xl font-bold text-gray-900">{totalScore}%</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-3">
                  <div className="h-3 rounded-full transition-all duration-500" style={{width: `${totalScore}%`, backgroundColor: totalScore >= 80 ? '#22c55e' : totalScore >= 50 ? '#f59e0b' : '#ef4444'}} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-500">{t('overview.coreInfo')}</span>
                    <span className="text-sm font-semibold text-gray-900">{coreScore}%</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-1.5">
                    <div className="h-1.5 rounded-full bg-blue-500 transition-all" style={{width: `${coreScore}%`}} />
                  </div>
                  <p className="text-[10px] text-gray-600 mt-1">{t('overview.coreInfoSub')}</p>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-500">{t('overview.additional')}</span>
                    <span className="text-sm font-semibold text-gray-900">{additionalScore}%</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-1.5">
                    <div className="h-1.5 rounded-full bg-cyan-500 transition-all" style={{width: `${additionalScore}%`}} />
                  </div>
                  <p className="text-[10px] text-gray-600 mt-1">{t('overview.additionalSub')}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card className="bg-white border-gray-200" data-testid="notifications-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-gray-900 text-sm flex items-center gap-2">
              <Bell className="h-3.5 w-3.5 text-yellow-400" /> {t('overview.notifications')}
              {notifications?.total > 0 && (
                <span className="text-[10px] bg-red-600 text-gray-900 rounded-full px-1.5 py-0.5 leading-none">{notifications.total}</span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!notifications?.notifications?.length ? (
              <p className="text-gray-500 text-sm py-4 text-center">{t('overview.noNotifications')}</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {notifications.notifications.slice(0, 8).map((n) => (
                  <div key={n.id || n.message} className={`flex items-start gap-3 py-2 border-b border-gray-200/50 last:border-0 ${
                    n.priority === 'high' ? 'bg-red-950/20 -mx-2 px-2 rounded' : ''
                  }`}>
                    <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      n.type === 'review' ? 'bg-amber-900/40' :
                      n.type === 'question' ? 'bg-blue-900/40' :
                      n.type === 'listing' ? 'bg-red-900/40' : 'bg-gray-800'
                    }`}>
                      {n.type === 'review' ? <Star className="h-3 w-3 text-amber-400" /> :
                       n.type === 'question' ? <MessageSquare className="h-3 w-3 text-blue-400" /> :
                       <AlertTriangle className="h-3 w-3 text-red-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-300 font-medium">{n.title}</p>
                      <p className="text-[11px] text-gray-500 truncate">{n.description}</p>
                    </div>
                    {n.priority === 'high' && (
                      <Badge variant="secondary" className="bg-red-900/40 text-red-300 text-[9px] flex-shrink-0">{t('overview.urgent')}</Badge>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ─── STAT CARDS (existing) ─── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stat-cards-section">
        <StatCard title={t('overview.stat.listings')} value={data.listings.synced} subtitle={t('overview.stat.listingsSub', { total: data.listings.total })} icon={MapPin} color="indigo" pct={listingsPct} testid="stat-listings" />
        <StatCard
          title={t('overview.stat.reviews')}
          value={data.reviews.total}
          subtitle={t('overview.stat.reviewsSub', { rating: data.reviews.average_rating })}
          icon={Star}
          color="amber"
          extra={<div className="flex gap-0.5 mt-1">{[1,2,3,4,5].map(s => <Star key={s} className={`h-3 w-3 ${s <= Math.round(data.reviews.average_rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-600'}`} />)}</div>}
          testid="stat-reviews"
        />
        <StatCard title={t('overview.stat.social')} value={data.social.total_posts} subtitle={t('overview.stat.socialSub')} icon={Share2} color="cyan" testid="stat-social" />
        <StatCard
          title={t('overview.stat.errors')}
          value={data.listings.errors}
          subtitle={t('overview.stat.errorsSub', { rate: data.listings.error_rate })}
          icon={data.listings.errors > 0 ? AlertTriangle : CheckCircle}
          color={data.listings.errors > 0 ? "red" : "green"}
          testid="stat-errors"
        />
      </div>

      {/* ─── REVIEW SOURCES + SOCIAL ACTIVITY ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-white border-gray-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-gray-900 text-sm">{t('overview.reviewSources')}</CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(data.reviews.sources).length === 0 ? (
              <p className="text-gray-500 text-sm py-4">{t('overview.noReviewsYet')}</p>
            ) : (
              <div className="space-y-3">
                {Object.entries(data.reviews.sources).map(([source, count]) => (
                  <div key={source} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{backgroundColor: source === 'Google' ? '#4285f4' : source === 'Facebook' ? '#1877f2' : source === 'Yelp' ? '#d32323' : '#6b7280'}} />
                      <span className="text-sm text-gray-300">{source}</span>
                    </div>
                    <Badge variant="secondary" className="bg-gray-800 text-gray-300 text-xs">{count}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white border-gray-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-gray-900 text-sm">{t('overview.recentSocialActivity')}</CardTitle>
          </CardHeader>
          <CardContent>
            {data.social.recent.length === 0 ? (
              <p className="text-gray-500 text-sm py-4">{t('overview.noPostsYet')}</p>
            ) : (
              <div className="space-y-3">
                {data.social.recent.map((post) => (
                  <div key={post.id || post.post_id || post.content} className="flex items-start gap-3 pb-3 border-b border-gray-200 last:border-0 last:pb-0">
                    <div className="w-8 h-8 rounded-lg bg-gray-800 flex items-center justify-center flex-shrink-0">
                      <Share2 className="h-3.5 w-3.5 text-gray-400" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-gray-300 truncate">{post.content}</p>
                      <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-500">
                        <span className="capitalize">{post.platform?.replace('_', ' ')}</span>
                        <span>{t('overview.viewsLabel', { count: post.engagement?.views || 0 })}</span>
                        <span>{t('overview.likesLabel', { count: post.engagement?.likes || 0 })}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ─── QUICK ACTIONS ─── */}
      <Card className="bg-white border-gray-200" data-testid="quick-actions-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-gray-900 text-sm flex items-center gap-2">
            <Zap className="h-3.5 w-3.5 text-yellow-400" /> {t('overview.quickActions')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <QuickAction icon={Star} label={t('overview.qaViewReviews')} color="amber" testid="qa-reviews" />
            <QuickAction icon={MapPin} label={t('overview.qaCheckListings')} color="blue" testid="qa-listings" />
            <QuickAction icon={Eye} label={t('overview.qaSeoRankings')} color="green" testid="qa-seo" />
            <QuickAction icon={Phone} label={t('overview.qaCallHistory')} color="purple" testid="qa-calls" />
          </div>
        </CardContent>
      </Card>

      {/* Geo-Fencing Upsell */}
      <GeoFencingUpsell client={client} getHeaders={getHeaders} variant="compact" />
    </div>
  );
}

function LeadCard({ title, value, icon: Icon, color, subtitle, pct, testid }) {
  const colors = {
    blue: 'text-blue-400 bg-blue-900/30 border-blue-900/30',
    emerald: 'text-emerald-400 bg-emerald-900/30 border-emerald-900/30',
    amber: 'text-amber-400 bg-amber-900/30 border-amber-900/30',
    red: 'text-red-400 bg-red-50 border-red-900/30',
  };
  return (
    <Card className="bg-white border-gray-200" data-testid={testid}>
      <CardContent className="pt-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[11px] text-gray-500 uppercase tracking-wider">{title}</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
            <p className="text-[11px] text-gray-400 mt-0.5">{subtitle}</p>
          </div>
          <div className={`p-2.5 rounded-xl ${colors[color] || colors.blue}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
        {pct !== undefined && (
          <div className="mt-2 flex items-center gap-2">
            <div className="flex-1 bg-gray-700 rounded-full h-1.5">
              <div className={`h-1.5 rounded-full transition-all ${
                color === 'emerald' ? 'bg-emerald-500' : color === 'amber' ? 'bg-amber-500' : color === 'red' ? 'bg-red-500' : 'bg-blue-500'
              }`} style={{width: `${Math.min(pct, 100)}%`}} />
            </div>
            <span className="text-[10px] text-gray-500">{pct}%</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ProductCard({ title, icon: Icon, status, statusLabel, metric, label, detail, testid }) {
  const statusColors = {
    active: { bg: 'bg-emerald-900/20', border: 'border-emerald-800/40', dot: 'bg-emerald-400', text: 'text-emerald-400' },
    warning: { bg: 'bg-amber-50', border: 'border-amber-800/40', dot: 'bg-amber-400', text: 'text-amber-400' },
    error: { bg: 'bg-red-900/20', border: 'border-red-800/40', dot: 'bg-red-400', text: 'text-red-400' },
  };
  const s = statusColors[status] || statusColors.active;
  return (
    <Card className={`${s.bg} ${s.border} border`} data-testid={testid}>
      <CardContent className="pt-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-900">{title}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full ${s.dot} animate-pulse`} />
            <span className={`text-[10px] uppercase tracking-wider font-medium ${s.text}`}>{statusLabel || status}</span>
          </div>
        </div>
        <p className="text-2xl font-bold text-gray-900">{metric}</p>
        <p className="text-[11px] text-gray-400 mt-0.5">{label}</p>
        <p className="text-[11px] text-gray-500 mt-1">{detail}</p>
      </CardContent>
    </Card>
  );
}

function QuickAction({ icon: Icon, label, color, testid }) {
  const colors = {
    amber: 'hover:bg-amber-50 hover:border-amber-800/40 text-amber-400',
    blue: 'hover:bg-blue-50 hover:border-blue-800/40 text-blue-400',
    green: 'hover:bg-emerald-900/20 hover:border-emerald-800/40 text-emerald-400',
    purple: 'hover:bg-purple-50 hover:border-purple-800/40 text-purple-400',
  };
  return (
    <button
      className={`flex flex-col items-center gap-2 p-4 rounded-lg border border-gray-200 bg-gray-900/50 transition-all ${colors[color]} cursor-pointer`}
      data-testid={testid}
    >
      <Icon className="h-5 w-5" />
      <span className="text-xs text-gray-400">{label}</span>
    </button>
  );
}

function StatCard({ title, value, subtitle, icon: Icon, color, pct, extra, testid }) {
  const colors = {
    indigo: 'text-blue-400 bg-blue-900/30',
    amber: 'text-amber-400 bg-amber-900/30',
    cyan: 'text-cyan-400 bg-cyan-900/30',
    red: 'text-red-400 bg-red-50',
    green: 'text-green-400 bg-green-50',
  };
  return (
    <Card className="bg-white border-gray-200" data-testid={testid}>
      <CardContent className="pt-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">{title}</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
            <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>
            {extra}
          </div>
          <div className={`p-2.5 rounded-xl ${colors[color] || colors.indigo}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
        {pct !== undefined && (
          <div className="mt-3 w-full bg-gray-700 rounded-full h-1.5">
            <div className="h-1.5 rounded-full bg-blue-500 transition-all" style={{width: `${pct}%`}} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function formatDuration(seconds) {
  if (!seconds) return '0s';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}
